import type {
  LabBulkDecideInput,
  LabBulkDecideResponse,
  LabRemoteConfigResponse,
  LabSeedIdResponse,
} from '@luxuryescapes/contract-svc-lab'

import config from 'constants/config'
import request, { authOptions } from 'api/requestUtils'

export const FUNNEL_OPTIMISER_API_TIMEOUT_MS = 2500

const getLabEndpointOverride = (): string | undefined => {
  const labApiHost = config.LAB_API_HOST
  if (!labApiHost) {
    return undefined
  }

  if (labApiHost.startsWith('http://') || labApiHost.startsWith('https://')) {
    return labApiHost
  }

  return `${config.SCHEME}://${labApiHost}`
}

type HeaderBannerEvaluateInput = {
  distinctId: string
  currentPath: string
  attributes?: Record<string, unknown>
}

const getFeatureFlagContext = (featureFlagContext: App.FeatureFlagContext | undefined) => {
  let attributes = undefined
  if (featureFlagContext) {
    const { utmCampaign, utmContent, utmMedium, utmSource, utmTerm, deviceName, regionCode } = featureFlagContext
    attributes = Object.fromEntries(
      Object.entries({
        utmCampaign,
        utmContent,
        utmMedium,
        utmSource,
        utmTerm,
        deviceName,
        regionCode,
      }).filter(([_, value]) => value != null && value !== ''),
    )
  }
  return attributes
}

export const getFeatureFlagDecisions = async(
  { keys, distinctId, featureFlagContext, token, additionalAttributes, forceRefresh, timeout }: {
    keys: Array<string>,
    distinctId: string,
    featureFlagContext?: App.FeatureFlagContext,
    token?: string,
    additionalAttributes?: Record<string, unknown>,
    forceRefresh?: boolean,
    timeout?: number,
  },
): Promise<Record<string, App.LabFeatureFlagResult>> => {
  const options = { ...authOptions(token), timeout }
  const baseAttributes = getFeatureFlagContext(featureFlagContext)
  const endpointOverride = getLabEndpointOverride()

  const input: LabBulkDecideInput = {
    keys,
    distinctId,
    attributes: { ...baseAttributes, ...additionalAttributes },
    forceRefresh,
  }
  const response = await request.post<LabBulkDecideResponse, LabBulkDecideInput>('/api/lab/decide', input, options, endpointOverride)
  if (!response) return {} as Record<string, App.LabFeatureFlagResult>
  return response.data
}

// preview endpoint does not require authentication
export const getLabPreview = async(previewId: string): Promise<Record<string, App.LabFeatureFlagResult>> => {
  const response = await request.get<{ data: Record<string, App.LabFeatureFlagResult> }>(`/api/lab/preview/${previewId}`, {}, getLabEndpointOverride())
  if (!response) return {} as Record<string, App.LabFeatureFlagResult>
  return response.data
}

export const getSeedIdFromApi = async(memberId: string): Promise<string | undefined> => {
  const response = await request.get<LabSeedIdResponse>(`/api/lab/seed-id?memberId=${encodeURIComponent(memberId)}`, {}, getLabEndpointOverride())
  return response?.data.seedId ?? undefined
}

export const getRemoteConfigFlags = async(
  keys: Array<string>,
): Promise<Record<string, App.LabRemoteConfigResult>> => {
  const response = await request.post<LabRemoteConfigResponse, { keys: Array<string> }>('/api/lab/remote-config', { keys }, {}, getLabEndpointOverride())
  if (!response) return {} as Record<string, App.LabRemoteConfigResult>
  return response.data
}

export const getHeaderBanners = async(
  { distinctId, currentPath, featureFlagContext, token }: {
    distinctId: string,
    currentPath: string,
    featureFlagContext?: App.FeatureFlagContext,
    token?: string,
  },
): Promise<App.LabHeaderBannersResult | undefined> => {
  const options = token ? authOptions(token) : {}
  const attributes = getFeatureFlagContext(featureFlagContext)
  const endpointOverride = getLabEndpointOverride()
  const input: HeaderBannerEvaluateInput = {
    distinctId,
    currentPath,
    attributes,
  }
  const response = await request.post<{ data: App.LabHeaderBannersResult }, HeaderBannerEvaluateInput>('/api/lab/header-banners', input, options, endpointOverride)
  return response?.data
}
